// components/DegreeAudit/Common/LoadingSkeletons.tsx
//
// Pure UI components with no business logic dependencies.
// Provides loading states while data is being fetched.

const ProgramHeaderSkeleton = () => {
  const pulseAnimation = 'animate-pulse bg-surface-sunken rounded';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 md:p-4 mb-4 md:mb-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2 md:space-x-3">
          <div className={`${pulseAnimation} w-4 h-4`}></div>
          <div className={`${pulseAnimation} w-4 md:w-5 h-4 md:h-5 rounded`}></div>
          <div className={`${pulseAnimation} h-6 md:h-8 w-48 md:w-64`}></div>
        </div>
        <div className={`${pulseAnimation} h-8 md:h-10 w-24 md:w-32 rounded-lg`}></div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="flex-1 mr-4">
          <div className={`${pulseAnimation} h-3 w-full rounded-full mb-2`}></div>
          <div className="flex items-center justify-between text-xs">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center space-x-1">
                <div className={`${pulseAnimation} w-2 h-2 rounded-full`}></div>
                <div className={`${pulseAnimation} h-3 w-12`}></div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <div className={`${pulseAnimation} h-4 w-12`}></div>
          <div className={`${pulseAnimation} h-4 w-12`}></div>
          <div className={`${pulseAnimation} h-6 w-20 rounded-full`}></div>
        </div>
      </div>

      <div className="text-xs">
        <div className={`${pulseAnimation} h-3 w-64`}></div>
      </div>
    </div>
  );
};

interface RequirementNodeSkeletonProps {
  depth?: number;
}

const RequirementNodeSkeleton = ({ depth = 0 }: RequirementNodeSkeletonProps) => {
  const pulseAnimation = 'animate-pulse bg-surface-sunken rounded';
  const indent = depth * 16;

  return (
    <div className="border border-gray-200 rounded-lg mb-2 overflow-hidden">
      <div className="p-3 md:p-4 bg-white">
        <div className="flex items-center justify-between">
          <div
            className="flex items-center space-x-2 md:space-x-3"
            style={{ marginLeft: `${indent}px` }}
          >
            <div className={`${pulseAnimation} w-4 h-4`}></div>
            <div className={`${pulseAnimation} w-4 md:w-5 h-4 md:h-5 rounded`}></div>
            <div>
              <div className={`${pulseAnimation} h-4 md:h-5 w-24 md:w-32 mb-1`}></div>
              <div className={`${pulseAnimation} h-3 md:h-4 w-36 md:w-48`}></div>
            </div>
          </div>
          <div className="w-24 md:w-32">
            <div className="flex items-center gap-1 md:gap-2">
              <div className="flex-1 bg-surface-sunken rounded-full h-2 overflow-hidden">
                <div className={`${pulseAnimation} h-full w-3/4 rounded-full`}></div>
              </div>
              <div className={`${pulseAnimation} h-3 md:h-4 w-8 md:w-10`}></div>
            </div>
          </div>
        </div>
      </div>

      {depth < 2 && Math.random() > 0.5 && (
        <div className="p-3 md:p-4 bg-white border-t border-gray-200">
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-2 md:p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className={`${pulseAnimation} w-4 h-4`}></div>
                    <div>
                      <div className={`${pulseAnimation} h-3 md:h-4 w-16 md:w-20 mb-1`}></div>
                      <div className={`${pulseAnimation} h-3 w-24 md:w-32`}></div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 md:space-x-2">
                    <div className={`${pulseAnimation} h-4 md:h-5 w-12 md:w-16 rounded`}></div>
                    <div className={`${pulseAnimation} h-4 md:h-5 w-16 md:w-20 rounded`}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const HierarchicalSkeleton = () => {
  const pulseAnimation = 'animate-pulse bg-surface-sunken rounded';

  return (
    <div className="space-y-4 md:space-y-6">
      <ProgramHeaderSkeleton />

      <div className="space-y-2">
        <RequirementNodeSkeleton depth={0} />
        <RequirementNodeSkeleton depth={1} />
        <RequirementNodeSkeleton depth={0} />
        <RequirementNodeSkeleton depth={1} />
      </div>

      <div className="border-t pt-4 md:pt-6 hidden md:block">
        <div className={`${pulseAnimation} h-5 md:h-6 w-24 md:w-32 mb-3 md:mb-4`}></div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div className="bg-blue-50 p-3 md:p-4 rounded">
            <div className={`${pulseAnimation} h-4 md:h-5 w-32 md:w-40 mb-2`}></div>
            <div className="space-y-1">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`${pulseAnimation} h-3 w-full`}></div>
              ))}
            </div>
          </div>

          <div className="bg-green-50 p-3 md:p-4 rounded">
            <div className={`${pulseAnimation} h-4 md:h-5 w-32 md:w-40 mb-2`}></div>
            <div className="space-y-1">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`${pulseAnimation} h-3 w-full`}></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HierarchicalSkeleton;
